import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PlusIcon } from '@heroicons/react/24/solid'
import { useUrlFilters } from '@/hooks/useUrlFilters'
import { Modal, Pagination, SectionPanel, Spinner } from '@/components/ui'
import DetailTemplate from '@/components/DetailTemplate'

import { fetchInterventions, setFilters, selectInterventionsInProgress, setInterventionInEdition } from './interventionsSlice'
import InterventionItem from './Intervention'
import Form from './form/Form'

const InterventionList = ({inProgress = false, skipPagination = false}) => {
  const dispatch = useDispatch()
  const SECTION_TITLE = "Suivi d'interventions en cours"
  
  // Initialiser les filtres dans le state Redux avant le premier rendu
  // Cela doit être fait avant que useUrlFilters ne soit appelé
  useEffect(() => {
    if (inProgress) {
      // Définir immédiatement les filtres pour éviter la première requête sans filtres
      const initialFilters = {statut: 'in_progress'}
      
      // Ajouter skip_pagination si nécessaire
      if (skipPagination) {
        initialFilters.skip_pagination = 'true'
      }
      
      dispatch(setFilters(initialFilters))
    }
  }, []) // Exécuté une seule fois au montage
  
  const interventions = useSelector(selectInterventionsInProgress)
  const filters = useSelector(state => state.interventions.filters)
  const pagination = useSelector((state) => state.interventions.pagination)
  const interventionInEdition = useSelector( state => state.interventions.inEdition)
  const isLoading = useSelector((state) => state.interventions.loadingStatus.fetchList)

  const { isOpen, handleShow, handleHide, dialogRef } = Modal.useModal()

  // Utiliser useUrlFilters avec les filtres du state Redux
  // À ce stade, si inProgress est true, filters devrait déjà contenir {statut: 'in_progress'}
  useUrlFilters({
    setFiltersAction: setFilters,
    fetchAction: fetchInterventions,
    filters,
    allowedParams: ['statut', 'page', 'limit', 'skip_pagination'],
    updateUrlParams: false,
    waitForDefaultFilters: inProgress // Indiquer que le hook doit attendre l'application des filtres par défaut si inProgress est true
  })

  const handlePageChange = (page) => {
    dispatch(setFilters({page}))
  }

  const AddNewInterventionButton = ({onEditClick}) => {
    const handleClick = async (event) => {
      event.preventDefault()
      event.stopPropagation()
      dispatch(setInterventionInEdition({}))
      onEditClick()
    }

    return (
      <div>
        <button 
          className="btn btn-sm btn-primary"
          title="Ajouter une nouvelle activité"
          onClick={handleClick}
        >
          <PlusIcon className="h-5 w-5 mr-1"/> Ajouter une intervention
        </button>
      </div>
    )
  }

  return (
    <DetailTemplate 
      bodyProps={{columns: 1, bgColor: 'white', className: 'p-0'}}
      modalContent={<Form />}
    >
    <SectionPanel>
      <SectionPanel.Title className='font-normal'>
        <div>
          <h2 className="text-2xl font-bold mb-2">
            {SECTION_TITLE}
          </h2>
        </div>
        <div className="flex items-right justify-between items-center mb-4">
          <AddNewInterventionButton onEditClick={handleShow}/>
        </div>
      </SectionPanel.Title>
      <SectionPanel.Body columns={1} bgColor='white' className='p-0'>
        {isLoading && <div className='text-center'><Spinner color='primary'/></div>}
        {!isLoading && interventions.length === 0 && (
          <div className='bg-gray-100 p-4'>
            <p>Aucune intervention en cours</p>
          </div>
        )}
        <div>
          {!isLoading && interventions.length > 0 && interventions.map(
            (intervention, index) => (
              <InterventionItem key={index} item={intervention} withBienAddress={true} />
            )
          )}
        </div>
        {pagination.totalPages > 1 && (
          <Pagination 
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </SectionPanel.Body>
    </SectionPanel>
    <Modal
      open={isOpen}
      onClose={handleHide}
      ref={dialogRef}
      backdrop={false}
      className="max-w-full rounded-none"
    >
      <Modal.Header showCloseButton={false} />
      <Modal.Body>
        { interventionInEdition && <Form onClose={handleHide} onSuccess={handleHide}/>}
      </Modal.Body>
    </Modal>
    </DetailTemplate>
  )
}

export default InterventionList